<template>
  <v-container fluid>
    <!-- Used to display orders list -->
    <h1>{{ $t('orders.order-list') }}</h1>

    <MessageBox type="info" class="mb-8" :closable="true">
      <h5>{{ $t('order.info.daysWarning-title3') }}</h5>
      <p>
        {{ $t('order.info.daysWarning-text3') }}
      </p>
    </MessageBox>

    <MessageBox type="alert" class="mb-8" v-if="showWarning">
      <h5>{{ $t('order.info.daysWarning-title') }}</h5>
      <p>
        {{ $t('order.info.daysWarning-text') }}
      </p>
    </MessageBox>

    <v-text-field
      class="search-field mb-6 mt-5"
      prepend-icon="$sl-search"
      single-line
      hide-details
      :label="$t('application.search-label')"
      v-model="search"
    ></v-text-field>

    <div class="button-container">
      <v-btn class="btn-label mb-6" :class="{'action': states.includes(1), 'application': !states.includes(1)}" @click="showNotApproved">
        <v-icon left>$sl-task-finger-show</v-icon>
        {{ $t('order.info.showUnhandledOrders') }}
      </v-btn>

      <v-btn class="btn-label mb-6 ml-4" :class="{'action': !states.includes(1), 'application': states.includes(1)}" @click="showPrevious">
        <v-icon left>$sl-task-list-approve</v-icon>
        {{ $t('order.info.showAllOrders') }}
      </v-btn>
    </div>

    <template v-if="hasDepartments($route.params.companyId)">
      <MessageBox type="info" class="mb-8" v-if="!isAdministrator">
        <p>
          {{ $t('order.info.exemption-messagebox') }}
          {{ getCurrentUserDepartments($route.params.companyId, isAdministrator) }}
        </p>
      </MessageBox>
      <div class="asterisk" v-if="tab === 0">{{ $t('order.info.groupAll') }}</div>
      <div class="asterisk" v-else>{{ $t('order.info.groupDepartment') }}</div>

      <v-tabs
        v-model="tab"
        class="mx-0"
        :fixed-tabs="$vuetify.breakpoint.xsOnly"
        :centered="$vuetify.breakpoint.xsOnly"
      >
        <v-tab key="1">{{ $t('labels.all') }}</v-tab>
        <v-tab key="2">{{ $t('company.sections') }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item key="1">
          <CompanyDistanceOrdersList :orders="filteredOrders" v-if="orders.length > 0" />
          <v-card flat v-else>
            <p>{{ $t('orders.order-list-empty') }}</p>
          </v-card>
        </v-tab-item>
        <v-tab-item key="2">
          <CompanyDistanceOrdersList
            :orders="filteredDepartmentOrders"
            departments
            v-if="departmentOrders.length > 0"
          />
          <v-card flat v-else>
            <p>{{ $t('orders.order-list-empty') }}</p>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>
    <template v-else>
      <CompanyDistanceOrdersList :orders="filteredOrders" v-if="orders.length > 0" />
      <v-card flat v-else>
        <p>{{ $t('orders.order-list-empty') }}</p>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import find from 'lodash/find'
import get from 'lodash/get'
import {mapActions, mapGetters} from 'vuex'
import SugarDate from 'sugar/date'
import {CompanyRoles} from '@/components/mixins'
import CompanyDistanceOrdersList from '@/components/Company/CompanyDistanceOrdersList'
import MessageBox from '@/components/Common/MessageBox'
import ActionButton from '@/components/Common/ActionButton'

// @group Company
// CompanyDistanceOrders component
export default {
  name: 'CompanyDistanceOrders',
  mixins: [CompanyRoles],
  components: {
    CompanyDistanceOrdersList,
    MessageBox,
    ActionButton
  },
  data() {
    return {
      orders: [],
      order: {},
      tab: null,
      search: '',
      showWarning: null,
      states: [1]
    }
  },

  watch: {
    /**
     * @vuese
     * Used to watch route and recall  the method fetchListData if the route changes
     */
    $route: 'fetchListData'
  },
  created() {
    this.fetchListData()
  },
  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isInstructor', 'isAdministrator']),
    ...mapGetters('basic', ['basicDistanceOrderStates', 'basicExtensionOrderStates']),

    departmentOrders: function () {
      const departmentOrders = this.orders.reduce((acc, order) => {
        const {workplace} = order
        if (!workplace) return acc
        const {departmentWorkplaces} = workplace
        departmentWorkplaces.forEach(workplace => {
          acc.push({
            ...order,
            department: workplace.department,
            departmentName: workplace.department.name
          })
        })
        return acc
      }, [])
      return departmentOrders
    },
    filteredOrders: function () {
      if (!this.search || !this.orders) return this.orders
      const fieldsToSearch = ['firstName', 'lastName', 'stateName']
      return this.orders.filter(item =>
        fieldsToSearch.some(field => get(item, field, '').toLowerCase().includes(this.search.toLowerCase()))
      )
      return this.orders
    },
    filteredDepartmentOrders: function () {
      if (!this.search || !this.departmentOrders) return this.departmentOrders
      const fieldsToSearch = ['firstName', 'lastName', 'stateName', 'departmentName']
      return this.departmentOrders.filter(item =>
        fieldsToSearch.some(field => get(item, field, '').toLowerCase().includes(this.search.toLowerCase()))
      )
    }
  },
  methods: {
    /**
     * @vuese
     * Used to get company distance orders
     * @arg The argument is an integer representing company id
     */
    setCookie(keyName) {
      //this.$cookies.set(keyName, this.currentUser.id, '2m')
    },

    fetchListData() {
      if (this.$route.params) {
        this.search = ''
        API.getCompanyOrders(Number(this.$route.params.companyId), this.states)
          .then(res => {
            this.orders = res.data.rows.filter(it => it.type !== 'PrintOrder');

            if (this.showWarning === null) {
              this.showWarning = res.data.rows.find(
                r =>
                  r.stateId === 1 &&
                  r.latestApprovalDate &&
                  get(r,'profession.daysUntilOrderApproval', 0) > 0 &&
                  SugarDate.Date(r.latestApprovalDate).daysFromNow() < Number(r.profession.daysUntilOrderApproval) / 2
              )
            }

            if (this.$route.params.distance_order_id) {
              let o = find(this.orders, ['id', Number(this.$route.params.distance_order_id)])
              o ? (this.order = o) : (this.order = {})
            }
          })
          .catch(error => {
            console.log(error);
            console.log('There was an error:', error.res)
          })
      }
    },
    showPrevious() {
      this.states = [2,3,4,5,6,7]
      this.fetchListData()
    },
    showNotApproved() {
      this.states = [1]
      this.fetchListData()
    }
  }
}
</script>

<style lang="scss">
.button-container {
  display: flex;
  flex-direction: row;

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    flex-direction: column;
    .btn-label {
      margin-left: 0px !important;
    }
  }
}
</style>
